import ChevronLeftIcon from "@assets/svg/ChevronLeftIcon";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@components/ui/dialog";
import { getPaymentHistoryDetails } from "@endpoints/stripe.service";
import { stripeRefund, walletRefund } from "@endpoints/wallet.service";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const REFUND_METHOD = {
  ORIGINAL: 1,
  WALLET: 2,
} as const;

const refundMethods = [
  {
    label: "Refund to original payment method",
    desc: "Your refund will be credited back to the card used for this job post. Estimated time: 5–7 business days",
    value: 1,
  },
  {
    label: "Refund to Comtracked Wallet",
    desc: "Get instant credit in your Comtracked virtual account. Use it for future job postings or services.",
    value: 2,
  },
];

const RefundSummary = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { state: jobDetails } = useLocation();

  // Local state
  const [refundMethod, setRefundMethod] = useState<number | undefined>();
  const [isRefunding, setIsRefunding] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [isPaymentDetailsLoading, setIsPaymentDetailsLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const isWalletRefund = refundMethod === REFUND_METHOD.WALLET;

  const fetchRefundPaymentDetails = useCallback(async () => {
    try {
      setIsPaymentDetailsLoading(true);
      const { data, success } = await getPaymentHistoryDetails({
        id: jobDetails?._id,
        type: "jobpost",
        by: "job",
      });
      if (success) {
        setPaymentDetails(() => (Array.isArray(data) ? data[0] : data));
      }
    } catch (error) {
      console.error("Error: fetching refund payment details", error);
    } finally {
      setIsPaymentDetailsLoading(false);
    }
  }, [jobDetails?._id]);

  const handleProceed = () => {
    if (!refundMethod) {
      enqueueSnackbar("Please select the refund method", { variant: "error" });
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmRefund = async () => {
    setShowConfirmModal(false);

    if (isWalletRefund) {
      try {
        setIsRefunding(true);
        const { success } = await walletRefund({
          stripePaymentIntentId: paymentDetails?.paymentIntentId,
          amountCents: paymentDetails?.totalAmount * 100,
          currency: "gbp",
        });
        if (success) {
          navigate("/referral/refunds");
          enqueueSnackbar("Amount refunded successfully", {
            variant: "success",
          });
        }
      } catch (error) {
        console.error("Error: refunding the amount.", error);
        enqueueSnackbar(error, { variant: "error" });
      } finally {
        setIsRefunding(false);
      }
    } else {
      try {
        setIsRefunding(true);
        const { success } = await stripeRefund({
          stripePaymentIntentId: paymentDetails?.paymentIntentId,
          amountCents: paymentDetails?.totalAmount * 100,
          currency: "gbp",
        });
        if (success) {
          navigate("/referral/refunds");
          enqueueSnackbar("Amount refunded successfully", {
            variant: "success",
          });
        }
      } catch (error) {
        console.error("Error: refunding the amount.", error);
        enqueueSnackbar(error, { variant: "error" });
      } finally {
        setIsRefunding(false);
      }
    }
  };

  useEffect(() => {
    fetchRefundPaymentDetails();
  }, [fetchRefundPaymentDetails]);

  console.log("payment details", paymentDetails);

  return (
    <div className="flex flex-col py-4 min-h-0">
      <button
        onClick={() => navigate("/requirements")}
        className="flex items-center self-start gap-1"
      >
        <ChevronLeftIcon className={"w-4 h-4"} fill="#4d4d4d" />
        <label className="text-xs font-medium text-charcoal cursor-pointer">
          Back to listing
        </label>
      </button>
      <div className="flex flex-1 min-h-0">
        {/* Left section */}
        <div className="w-1/2 flex flex-col flex-1 min-h-0 border-r px-6 py-4 overflow-y-scroll no-scrollbar">
          <span className="font-bold text-sm pb-3">Refund summary</span>
          <div className="flex flex-col py-4 border-y gap-2">
            <div className="flex items-center text-xs gap-2">
              <span className="font-medium text-light-black w-32">
                Reference:
              </span>
              <span className="font-medium text-xs text-charcoal">
                {jobDetails?.refNo}
              </span>
            </div>
            <div className="flex items-center text-xs gap-2">
              <span className="font-medium text-light-black w-32">Title:</span>
              <span className="font-medium text-xs text-charcoal">
                {jobDetails?.title}
              </span>
            </div>
            <div className="flex items-center text-xs gap-2">
              <span className="font-medium text-light-black w-32">
                Job closed date:
              </span>
              <span className="font-medium text-xs text-charcoal">
                Immediately
              </span>
            </div>
          </div>

          {/* Refund method */}
          <div className="flex flex-col mt-6">
            <span className="font-bold text-sm pb-1.5 border-b mb-3">
              Refund method
            </span>
            <div className="flex flex-col gap-6">
              {refundMethods?.map((refund) => {
                const isSelected = refund?.value === refundMethod;
                return (
                  <button
                    key={refund?.value}
                    onClick={() => setRefundMethod(refund?.value)}
                    className="flex gap-3"
                  >
                    <div className="h-5 w-5 rounded-full border-2 border-primary flex justify-center items-center">
                      {isSelected ? (
                        <div className="h-3 w-3 rounded-full bg-primary" />
                      ) : null}
                    </div>
                    <div className="flex flex-col flex-1 gap-2.5">
                      <span className="font-medium text-left text-xs">
                        {refund?.label}
                      </span>
                      <span className="font-medium text-left text-xs text-light-black">
                        {refund?.desc}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        {/* Right Section */}
        <div className="w-1/2 flex flex-col flex-1 min-h-0 px-6 py-4 overflow-y-scroll no-scrollbar">
          <span className="font-bold text-sm pb-3">Refund Amount Details</span>

          {/* Total */}
          <div className="flex flex-col gap-2 py-4 border-y">
            <div className="flex items-center font-medium text-light-black text-xs">
              <span className="w-36">Total</span>
              <div className="flex items-center gap-3">
                £
                <div className="flex items-center pl-3 rounded-md h-9 w-28 border">
                  {isPaymentDetailsLoading ? (
                    <LoadingAnimation color="#2e2e2e" className={"w-4 h-4"} />
                  ) : paymentDetails?.totalAmount != null ? (
                    Number(paymentDetails.totalAmount).toFixed(2)
                  ) : (
                    "—"
                  )}
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleProceed}
            disabled={isRefunding}
            type="button"
            className="flex items-center justify-center gap-3 bg-primary rounded-md mt-6 text-white font-semibold py-3 hover:bg-black text-sm"
          >
            Proceed {isRefunding ? <LoadingAnimation /> : null}
          </button>
        </div>
      </div>

      {/* Confirmation dialog */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="bg-white">
          <DialogTitle className="text-lg text-left !font-semibold">
            Confirm refund
          </DialogTitle>
          <p className="text-center text-xs font-medium text-light-black px-2 py-3">
            You're about to refund
            {paymentDetails?.totalAmount != null
              ? ` £${Number(paymentDetails.totalAmount).toFixed(2)}`
              : ""}{" "}
            for{" "}
            <span className="text-charcoal">
              {jobDetails?.title || "this job post"}
            </span>{" "}
            via{" "}
            <span className="text-charcoal">
              {isWalletRefund
                ? "your Comtracked Wallet"
                : "the original payment method"}
            </span>
            . This action cannot be undone.
          </p>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setShowConfirmModal(false)}
              className="flex flex-1 items-center justify-center rounded-md border py-2.5 font-semibold text-sm text-charcoal hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmRefund}
              disabled={isRefunding}
              className="flex flex-1 items-center justify-center gap-2 rounded-md bg-primary py-2.5 font-semibold text-sm text-white hover:bg-black"
            >
              Confirm refund {isRefunding ? <LoadingAnimation /> : null}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RefundSummary;
